import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { Check, Sparkles, Loader2, Star, X, Crown, ArrowLeft, Coins, Shield, Globe, ChevronDown, Users, Zap, Wand2, Layers, Box, Video, HardDrive, AlertTriangle } from 'lucide-react';
import tierService, { Tier } from '../services/tierService';
import adminPricingService, { PricingPlanResponse } from '../services/adminPricingService';
import paymentService from '../services/paymentService';
import { useAuth } from '../contexts/AuthContext';
import { useUserTier } from '../hooks/useUserTier';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

import Navbar from '../components/layout/Navbar';
import ModernNavbar from '../components/layout/ModernNavbar';
import LightThemeWrapper from '../components/layout/LightThemeWrapper';
import ProfileDropdown from '../components/layout/ProfileDropdown';
import DynamicCreditExplanation from '../components/pricing/DynamicCreditExplanation';
import CreditPackages from '../components/billing/CreditPackages';
import WaitlistModal from '../components/pricing/WaitlistModal';
import StructuredFAQ from '../components/seo/StructuredFAQ';
import { pricingFAQs } from '../data/faqData';
import { div } from 'framer-motion/client';

// Icon mapping function
const getIconComponent = (iconName?: string) => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    Users,
    Zap,
    Crown,
    Coins,
    Wand2,
    Layers,
    Box,
    Video,
    HardDrive,
    Shield,
    Sparkles
  };

  // Default to Users if no icon specified or icon not found
  const IconComponent = iconMap[iconName || 'Users'] || Users;
  return <IconComponent className="w-5 h-5 text-white" />;
};

// Simplified navbar for pricing page
const PricingNavbar: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTier, isFreeTier } = useUserTier();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/logo-light.png"
                alt="Quixocial"
                className="h-8 md:h-10 w-auto"
              />
            </Link>
          </div>

          {/* Right side - Tier, Theme, Profile */}
          <div className="flex items-center space-x-4">
            {/* Credits Display - Hidden as requested */}
            {/* {user && user.credits !== undefined && (
              <div className="flex items-center space-x-2 bg-gradient-to-r from-emerald-50 to-teal-50 px-3 py-2 rounded-xl border border-emerald-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                <Coins className="h-4 w-4 text-emerald-600" />
                <span className="font-bold text-sm text-emerald-700">
                  {user.credits.toFixed(0)}
                </span>
              </div>
            )} */}

            {/* Tier Badge */}
            {currentTier && (
              <div
                className={`
                  flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium
                  ${isFreeTier
                    ? 'bg-gray-100 text-gray-600'
                    : 'text-blue-700 border border-blue-200/50'
                  }
                `}
                style={!isFreeTier ? { background: 'linear-gradient(to right, rgba(48, 179, 221, 0.1), rgba(74, 41, 138, 0.1))' } : {}}
              >

                <span className="hidden sm:inline">{currentTier.display_name}</span>
              </div>
            )}

            <ProfileDropdown />
          </div>
        </div>
      </div>
    </nav>
  );
};

const PricingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [waitlistModal, setWaitlistModal] = useState<{
    isOpen: boolean;
    tierName: string;
    featureName: string;
  }>({
    isOpen: false,
    tierName: '',
    featureName: ''
  });
  const [downgradeConfirm, setDowngradeConfirm] = useState<{
    isOpen: boolean;
    tier: any | null;
  }>({
    isOpen: false,
    tier: null
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const { currentTier, isFreeTier } = useUserTier();

  // Track pricing page view
  useEffect(() => {
    const trackPricingView = async () => {
      try {
        const { trackEvent } = await import('../lib/analytics');
        const { trackViewContent } = await import('../lib/metaPixel');
        trackEvent('pricing_viewed');
        trackViewContent('pricing_page', 'Pricing Page View');
      } catch (error) {
        console.error('Failed to track pricing page view:', error);
      }
    };
    
    trackPricingView();
  }, []);

  // Handle auto-checkout after signup
  useEffect(() => {
    const shouldCheckout = searchParams.get('checkout');
    const tierId = searchParams.get('tier');
    const billing = searchParams.get('billing');
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const sessionId = searchParams.get('session_id');

    // Handle successful payment
    if (success === 'true' && sessionId) {
      toast.success('Payment successful! Your subscription is now active.', {
        duration: 5000,
        position: 'top-center',
      });
      // Clean up URL
      navigate('/pricing', { replace: true });
      return;
    }

    // Handle canceled payment
    if (canceled === 'true') {
      toast.error('Payment was canceled. You can try again anytime.', {
        duration: 4000,
        position: 'top-center',
      });
      // Clean up URL
      navigate('/pricing', { replace: true });
      return;
    }

    // Clean up URL if returning from Stripe (has session_id but no success flag)
    if (sessionId && !success) {
      navigate('/pricing', { replace: true });
      return;
    }

    if (shouldCheckout === 'true' && tierId && isAuthenticated) {
      // Trigger checkout for the selected tier
      const triggerCheckout = async () => {
        try {
          // Track checkout initiation
          const { trackInitiateCheckout } = await import('../lib/metaPixel');
          trackInitiateCheckout(); // We don't have amount here, so track without value
          
          const response = await paymentService.createSubscriptionCheckout({
            tier_id: tierId,
            billing_period: billing || 'monthly'
          });

          if (response.checkout_url) {
            window.location.href = response.checkout_url;
          }
        } catch (error) {
          console.error('Failed to create checkout:', error);
          toast.error('Failed to start checkout. Please try again.');
        }
      };

      triggerCheckout();
    }
  }, [searchParams, isAuthenticated, navigate]);

  // Fallback tiers in case API fails
  const fallbackTiers: Tier[] = [
    {
      id: 'free',
      name: 'free',
      display_name: 'Free',
      description: 'Perfect for getting started with basic features',
      price_monthly: 0,
      price_yearly: 0,
      is_active: true,
      sort_order: 1,
      signup_credits: 10,
      monthly_credits: 5,
      created_at: '',
      updated_at: ''
    },
    {
      id: 'pro',
      name: 'pro',
      display_name: 'Pro',
      description: 'For growing businesses with advanced features',
      price_monthly: 29.99,
      price_yearly: 299.99,
      is_active: true,
      sort_order: 2,
      signup_credits: 50,
      monthly_credits: 100,
      created_at: '',
      updated_at: ''
    },
    {
      id: 'enterprise',
      name: 'enterprise',
      display_name: 'Enterprise',
      description: 'Custom solutions for large organizations with dedicated support',
      price_monthly: 99.99,
      price_yearly: 999.99,
      is_active: true,
      sort_order: 3,
      signup_credits: 200,
      monthly_credits: 500,
      created_at: '',
      updated_at: ''
    }
  ];

  // Fetch tiers from API (public endpoint, no auth required)
  const { data: apiTiers, isLoading, error } = useQuery<Tier[]>({
    queryKey: ['public-tiers', 'active'],
    queryFn: () => tierService.getPublicTiers(true), // Only active tiers, public endpoint
    retry: 1, // Only retry once to avoid long loading times
    staleTime: 30 * 1000, // Consider data stale after 30 seconds (more aggressive)
    cacheTime: 2 * 60 * 1000, // Keep in cache for 2 minutes (shorter)
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchInterval: 60 * 1000, // Refetch every minute
  });

  // Fetch dynamic pricing features (public endpoint, no auth required)
  const { data: pricingPlans, isLoading: featuresLoading } = useQuery<PricingPlanResponse>({
    queryKey: ['pricing-plans'],
    queryFn: adminPricingService.getPricingPlans,
    retry: 1,
    staleTime: 30 * 1000,
    cacheTime: 2 * 60 * 1000,
  });

  // Use API tiers (which include credits) and merge with pricing plans features if available
  const tiers = apiTiers || fallbackTiers;

  // Debug logging to see what we're getting from the API
  console.log('API Tiers:', apiTiers);
  console.log('Pricing Plans:', pricingPlans);
  console.log('Final Tiers:', tiers);

  // Get features for a specific tier from dynamic pricing data
  // Helper function to expand inherited features
  const expandInheritedFeatures = (features: any[], currentTierId: string, visited = new Set()): any[] => {
    if (visited.has(currentTierId)) {
      return []; // Prevent circular inheritance
    }
    visited.add(currentTierId);

    const expandedFeatures: any[] = [];

    features.forEach(feature => {
      const featureText = typeof feature === 'string' ? feature :
        (feature?.display_text || feature?.pricing_feature?.name || '');

      // Check if this is an inheritance feature
      const isInheritanceFeature = featureText.toLowerCase().includes('all features from') ||
        featureText.toLowerCase().includes('everything from') ||
        featureText.toLowerCase().includes('lower tier') ||
        featureText.toLowerCase().includes('base tier') ||
        featureText.toLowerCase().includes('previous tier');

      if (isInheritanceFeature) {
        // Find which tier to inherit from
        let inheritFromTier = null;

        // Look for specific tier names in the feature text
        const tierNames = ['free', 'starter', 'creator', 'pro', 'enterprise'];
        for (const tierName of tierNames) {
          if (featureText.toLowerCase().includes(tierName)) {
            inheritFromTier = tiers.find(t => t.name.toLowerCase() === tierName);
            break;
          }
        }

        // If no specific tier mentioned, try to find the previous tier
        if (!inheritFromTier && (featureText.toLowerCase().includes('lower tier') ||
          featureText.toLowerCase().includes('base tier') ||
          featureText.toLowerCase().includes('previous tier'))) {
          const currentTier = tiers.find(t => t.id === currentTierId);
          if (currentTier) {
            const currentIndex = tiers.findIndex(t => t.id === currentTierId);
            if (currentIndex > 0) {
              inheritFromTier = tiers[currentIndex - 1];
            }
          }
        }

        // Get features from the inherited tier
        if (inheritFromTier) {
          const inheritedFeatures = getTierFeaturesRaw(inheritFromTier.id);
          const expandedInherited = expandInheritedFeatures(inheritedFeatures, inheritFromTier.id, visited);
          expandedFeatures.push(...expandedInherited);
        }
      } else {
        // Regular feature, add it directly
        expandedFeatures.push(feature);
      }
    });

    return expandedFeatures;
  };

  // Get raw features without expansion (to prevent infinite recursion)
  const getTierFeaturesRaw = (tierId: string) => {
    if (pricingPlans) {
      // Find the tier in pricing plans by matching ID or name
      const pricingTier = pricingPlans.tiers.find(t => t.id === tierId);
      if (pricingTier?.features) {
        return pricingTier.features;
      }

      // If not found by ID, try to match by name
      const apiTier = tiers.find(t => t.id === tierId);
      if (apiTier) {
        const pricingTierByName = pricingPlans.tiers.find(t => t.name === apiTier.name);
        if (pricingTierByName?.features) {
          return pricingTierByName.features;
        }
      }
    }

    // Fallback to hardcoded features if dynamic data not available
    const tierName = tiers.find(t => t.id === tierId)?.name || '';
    const fallbackFeatures: Record<string, string[]> = {
      free: [
        '2 connected social accounts',
        '1 team member',
        'Basic content scheduling',
        'Limited AI generations',
        'Community support'
      ],
      starter: [
        'Everything from Free',
        '5 connected social accounts',
        '3 team members',
        'Advanced AI generations',
        'Email support'
      ],
      pro: [
        'Everything from Starter',
        '10 connected social accounts',
        '5 team members',
        'Unlimited AI generations',
        'Social analytics',
        'Priority support'
      ],
      enterprise: [
        'Everything from Pro',
        'Unlimited social accounts',
        'Unlimited team members',
        'Custom integrations',
        'Dedicated account manager'
      ]
    };

    return fallbackFeatures[tierName] || [];
  };

  // Function for pricing cards - shows inheritance as-is (no expansion)
  const getTierFeaturesForDisplay = (tierId: string) => {
    return getTierFeaturesRaw(tierId);
  };

  // Function for comparison table - expands inheritance
  const getTierFeaturesExpanded = (tierId: string) => {
    const rawFeatures = getTierFeaturesRaw(tierId);
    return expandInheritedFeatures(rawFeatures, tierId);
  };

  // Main function that returns expanded features (for backward compatibility)
  const getTierFeatures = (tierId: string) => {
    return getTierFeaturesExpanded(tierId);
  };



  const faqs = [
    {
      question: 'Can I change my plan later?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time from your account settings. Changes will be prorated and you\'ll only pay the difference.',
    },
    {
      question: 'Do you offer a free trial?',
      answer: 'No, we offer a better option with a Free plan that has fewer features than our paid plans',
    },
    {
      question: 'How does billing work?',
      answer: 'We bill monthly or annually based on your chosen plan. Annual plans come with a significant discount. All payments are processed securely through Stripe.',
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Absolutely! You can cancel your subscription at any time. Your account will remain active until the end of your current billing period, and you\'ll retain access to all your content.',
    },
    {
      question: 'Do you support team collaboration?',
      answer: 'Yes, our Pro and Enterprise plans include team collaboration features with role-based permissions, shared workspaces, and client management tools.',
    },
    {
      question: 'What platforms do you support?',
      answer: 'We support all major social media platforms including Instagram, Facebook, X, LinkedIn, TikTok, YouTube, and Pinterest. We\'re constantly adding new platform integrations.',
    },
    {
      question: 'Is my data secure?',
      answer: 'Security is our top priority. We use enterprise-grade encryption, secure cloud storage, and comply with GDPR and other privacy regulations. Your content and data are always protected.',
    },
    {
      question: 'Do you offer custom enterprise solutions?',
      answer: 'Yes, we offer custom enterprise solutions with dedicated support, custom integrations, and advanced analytics. Contact our sales team for more information.',
    },
  ];

  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Handle downgrade confirmation
  const handleDowngradeConfirm = async () => {
    if (!downgradeConfirm.tier) return;

    const tier = downgradeConfirm.tier;

    try {
      console.log('🔽 Starting downgrade process for tier:', tier.id);
      const result = await paymentService.downgradeTier(tier.id);

      // Close dialog
      setDowngradeConfirm({ isOpen: false, tier: null });

      // Show success toast
      toast.success(
        `Downgrade scheduled! ${result.message}`,
        {
          duration: 5000,
          position: 'top-center',
          style: {
            background: '#10B981',
            color: '#fff',
            padding: '16px',
            borderRadius: '8px',
          },
        }
      );

      // Redirect to billing dashboard to see the scheduled change
      setTimeout(() => navigate('/app/billing'), 1500);
    } catch (error: any) {
      console.error('❌ Downgrade failed:', error);

      // Close dialog
      setDowngradeConfirm({ isOpen: false, tier: null });

      toast.error(
        `Failed to schedule downgrade: ${error.response?.data?.detail || error.message}`,
        {
          duration: 5000,
          position: 'top-center',
        }
      );
    }
  };

  // Get all unique features from all tiers for comparison (excluding inheritance features)
  const getAllUniqueFeatures = () => {
    if (pricingPlans?.features?.length) {
      return pricingPlans.features.map(f => f.name).filter(name =>
        name !== 'AI-Assisted Post Creation' && name !== 'AI Video Creation'
      );
    }

    // Fallback: extract features from tier data, excluding inheritance features
    const allFeatureSet = new Set<string>();
    tiers.forEach(tier => {
      const tierFeatures = getTierFeatures(tier.id);
      tierFeatures.forEach(feature => {
        // Skip inheritance features (show_in_comparison = false)
        if (typeof feature === 'object' && feature?.show_in_comparison === false) {
          return;
        }

        // Skip features that look like inheritance features by name
        const featureName = typeof feature === 'string' ? feature :
          feature?.display_text || feature?.pricing_feature?.name || '';

        if (featureName.toLowerCase().includes('all features from') ||
          featureName.toLowerCase().includes('lower tier') ||
          featureName.toLowerCase().includes('base tier') ||
          featureName.toLowerCase().includes('previous tier')) {
          return;
        }

        if (typeof feature === 'string') {
          if (feature !== 'AI-Assisted Post Creation' && feature !== 'AI Video Creation') {
            allFeatureSet.add(feature);
          }
        } else if (feature?.pricing_feature?.name) {
          if (feature.pricing_feature.name !== 'AI-Assisted Post Creation' && feature.pricing_feature.name !== 'AI Video Creation') {
            allFeatureSet.add(feature.pricing_feature.name);
          }
        } else if (feature?.display_text) {
          if (feature.display_text !== 'AI-Assisted Post Creation' && feature.display_text !== 'AI Video Creation') {
            allFeatureSet.add(feature.display_text);
          }
        }
      });
    });

    // If still no features, add some basic ones for comparison
    if (allFeatureSet.size === 0) {
      return [
        'Basic content scheduling',
        'AI generations',
        'Analytics',
        'Support',
        'Team collaboration',
        'Custom integrations',
        'Advanced security'
      ];
    }

    return Array.from(allFeatureSet);
  };

  const allFeatures = getAllUniqueFeatures();

  // Define which features should show limits vs ✓/✗
  const getFeatureDisplay = (featureName: string, tier: any) => {
    // First check the tier's own features (non-inherited)
    const ownFeatures = getTierFeaturesRaw(tier.id);
    let feature = ownFeatures.find(f => {
      if (typeof f === 'string') {
        return f === featureName || f.includes(featureName) || featureName.includes(f);
      } else if (f?.pricing_feature?.name) {
        return f.pricing_feature.name === featureName;
      } else if (f?.display_text) {
        return f.display_text === featureName;
      }
      return false;
    });

    // If not found in own features, check expanded features (inherited)
    if (!feature) {
      const tierFeatures = getTierFeaturesExpanded(tier.id);
      feature = tierFeatures.find(f => {
        if (typeof f === 'string') {
          return f === featureName || f.includes(featureName) || featureName.includes(f);
        } else if (f?.pricing_feature?.name) {
          return f.pricing_feature.name === featureName;
        } else if (f?.display_text) {
          return f.display_text === featureName;
        }
        return false;
      });
    }

    // Usage-based features that should show limits/numbers
    const usageFeatures = [
      'image generation', 'image to 3d', '3d model', 'video generation', 'ai generation',
      'post creation', 'post scheduling', 'team member', 'storage', 'credit', 'account',
      'social account', 'connected account', 'user', 'project', 'export', 'download'
    ];

    const isUsageFeature = usageFeatures.some(usage =>
      featureName.toLowerCase().includes(usage.toLowerCase())
    );

    // If feature not found
    if (!feature) {
      // For usage features, show 0 or unavailable
      if (isUsageFeature) {
        return { type: 'unavailable', display: <span className="font-semibold text-red-500">0</span> };
      }
      // For access features, show ✗
      return { type: 'unavailable', display: <X className="w-5 h-5 text-red-400 mx-auto" /> };
    }

    // Check if feature has a value (limit)
    const featureValue = typeof feature === 'object' ? feature.value : null;

    // Handle usage-based features with limits
    if (isUsageFeature) {
      if (featureValue) {
        const value = parseInt(featureValue);
        if (value === -1) return { type: 'unlimited', display: <span className="font-semibold text-green-600">Unlimited</span> };
        if (value === 0) return { type: 'unavailable', display: <span className="font-semibold text-red-500">0</span> };
        return { type: 'limit', display: <span className="font-semibold text-black">{value.toLocaleString()}</span> };
      }

      // If no value specified for usage feature, try to extract from text
      const featureText = typeof feature === 'string' ? feature :
        (feature?.display_text || feature?.pricing_feature?.name || '');

      // Look for numbers in the feature text
      const numberMatch = featureText.match(/(\d+)/);
      if (numberMatch) {
        const value = parseInt(numberMatch[1]);
        return { type: 'limit', display: <span className="font-semibold text-black">{value.toLocaleString()}</span> };
      }

      // Look for "unlimited" in text
      if (featureText.toLowerCase().includes('unlimited')) {
        return { type: 'unlimited', display: <span className="font-semibold text-green-600">Unlimited</span> };
      }

      // Default for usage features without clear limits
      return { type: 'included', display: <Check className="w-5 h-5 text-green-500 mx-auto" /> };
    }

    // Access-based features (show ✓/✗)
    const isIncluded = typeof feature === 'object' ? feature.is_included !== false : true;
    return {
      type: isIncluded ? 'included' : 'unavailable',
      display: isIncluded ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : <X className="w-5 h-5 text-red-400 mx-auto" />
    };
  };

  return (
    <LightThemeWrapper>
      <div className="min-h-screen bg-white">
        {isAuthenticated ? (
          <PricingNavbar />
        ) : (
          <Navbar activePage="pricing" />
        )}

        <div className="pt-24 px-4 sm:px-6 lg:px-8"> {/* Added more space from navbar */}
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              {/* Header with Back Button */}
              <div className="mb-6 relative flex items-center justify-center">
                {/* Back to Dashboard Button - Left side */}
                {isAuthenticated && (
                  <div className="absolute left-0">
                    <Button
                      onClick={() => navigate('/app/dashboard')}
                      variant="outline"
                      size="lg"
                      className="flex items-center space-x-3 px-6 py-3 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span className="font-medium">Back to Dashboard</span>
                    </Button>
                  </div>
                )}

                {/* Centered Badge */}
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border border-blue-200" style={{ background: 'linear-gradient(to right, rgba(48, 179, 221, 0.1), rgba(74, 41, 138, 0.1))' }}>
                  <span style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    Simple, Transparent Pricing
                  </span>
                </span>
              </div>
              <h1 className="text-4xl font-bold sm:text-5xl lg:text-6xl text-gray-900 mb-6">
                <span className="block">Plans that</span>
                <span style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Scale with You
                </span>
              </h1>
              <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-600 leading-relaxed">
                From solo creators to enterprise teams, find the perfect plan to amplify your social media presence.
              </p>


            </div>

            {/* Credit System Explanation */}
            <div className="mt-10 flex justify-center">
              <DynamicCreditExplanation
                maxItems={4}
                className="bg-white/80 border border-blue-200/50 shadow-lg hover:shadow-xl transition-shadow duration-300 text-gray-800"
              />
            </div>

            {/* Monthly/Yearly Toggle */}
            <div className="mt-8 flex justify-center">
              {/* Center the toggle buttons */}
              <div className="inline-flex rounded-md shadow-sm">
                <button
                  type="button"
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md focus:z-10 focus:outline-none focus:ring-2 focus:ring-blue-500 ${billingCycle === 'monthly'
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  style={billingCycle === 'monthly' ? { background: 'linear-gradient(to right, #30b3dd, #4a298a)' } : {}}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle('yearly')}
                  className={`-ml-px px-4 py-2 text-sm font-medium rounded-r-md focus:z-10 focus:outline-none focus:ring-2 focus:ring-blue-500 ${billingCycle === 'yearly'
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  style={billingCycle === 'yearly' ? { background: 'linear-gradient(to right, #30b3dd, #4a298a)' } : {}}
                >
                  Yearly (Save 20%)
                </button>
              </div>
            </div>

            {/* Loading State */}
            {(isLoading || featuresLoading) && (
              <div className="mt-16 flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}

            {/* Fallback Data Notice */}
            {error && tiers.length > 0 && (
              <div className="mt-8 text-center">
                <p className="text-sm text-gray-500">
                  Showing standard pricing. Contact us for the latest offers.
                </p>
              </div>
            )}



            {/* Error State - only show if no fallback tiers */}
            {error && !tiers.length && (
              <div className="mt-16 text-center">
                <p className="text-red-600">Failed to load pricing tiers. Please try again later.</p>
              </div>
            )}

            {/* No Tiers Available */}
            {tiers.length === 0 && (
              <div className="mt-16 text-center">
                <p className="text-gray-600">No pricing tiers available at the moment.</p>
              </div>
            )}



            {/* Pricing Tiers */}
            {tiers.length > 0 && (
              <div id="pricing-cards" className="mt-16 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-3 xl:grid-cols-4 lg:gap-x-8">
                {tiers
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((tier) => {
                    const isPopular = tier.name === 'pro' && !isAuthenticated; // Only show "Most Popular" for non-authenticated users
                    const tierFeatures = getTierFeaturesForDisplay(tier.id);

                    const formatPrice = (price?: number, tierName?: string) => {
                      if (tierName === 'free') return 'Free';
                      if (!price || price === 0) return 'Coming Soon';
                      return `${price.toFixed(2)}`;
                    };

                    // If user is authenticated but no currentTier, assume they're on free tier
                    const effectiveCurrentTier = currentTier || (isAuthenticated ? { name: 'free', id: 'free', price_monthly: 0 } : null);

                    const isCurrentPlan = isAuthenticated && effectiveCurrentTier && (
                      effectiveCurrentTier.name === tier.name ||
                      effectiveCurrentTier.id === tier.id ||
                      (effectiveCurrentTier.name?.toLowerCase() === tier.name?.toLowerCase())
                    );

                    // Determine if this is an upgrade or downgrade
                    const currentPrice = effectiveCurrentTier?.price_monthly || 0;
                    const tierPrice = tier.price_monthly || 0;
                    const isUpgrade = isAuthenticated && tierPrice > currentPrice;
                    const isDowngrade = isAuthenticated && tierPrice < currentPrice && tierPrice >= 0;

                    const getButtonText = (tier: any) => {
                      if (isCurrentPlan) return 'Current Plan';

                      // Check if tier has pricing set to 0 (coming soon)
                      const hasNoPricing = tier.price_monthly === 0 && tier.price_yearly === 0 && tier.name !== 'free';

                      // For non-authenticated users: Use custom button text if available
                      if (!isAuthenticated) {
                        // PRIORITY 1: Use custom button text if available and button is enabled
                        if (tier.is_button_enabled !== false && tier.button_text) {
                          return tier.button_text;
                        }

                        // PRIORITY 2: Default text for public
                        if (tier.name === 'free') return 'Get Started Free';
                        if (hasNoPricing) return 'Join Waitlist';
                        return 'Start Free Trial';
                      }

                      // For logged-in users: ALWAYS use dynamic text (ignore custom button_text)
                      if (hasNoPricing) return 'Join Waitlist';
                      if (isUpgrade) return 'Upgrade';
                      if (isDowngrade) {
                        if (tier.name === 'free') return 'Downgrade to Free';
                        return 'Downgrade';
                      }
                      if (tier.name === 'free') return 'Back to Dashboard';
                      return 'Upgrade Plan';
                    };

                    const getButtonLink = (tier: any) => {
                      if (isCurrentPlan) return '#';

                      // Use custom button URL if available and button is enabled
                      if (tier.is_button_enabled && tier.button_url) return tier.button_url;

                      // Check if tier has pricing set to 0 (coming soon)
                      const hasNoPricing = tier.price_monthly === 0 && tier.price_yearly === 0 && tier.name !== 'free';

                      // Fallback to default logic
                      if (!isAuthenticated) {
                        if (tier.name === 'free') return '/register';
                        if (hasNoPricing) return '#'; // Will trigger waitlist modal
                        // Redirect to register with tier info to redirect back after signup
                        return `/register?redirect=/pricing&tier=${tier.id}&billing=${billingCycle}`;
                      }
                      // For logged-in users
                      if (tier.name === 'free') return '/app/dashboard';
                      if (hasNoPricing) return '#'; // Will trigger waitlist modal
                      return '#'; // Disable upgrade buttons for now since payment isn't implemented
                    };

                    const handleButtonClick = async (tier: any, e: React.MouseEvent) => {
                      console.log('🎯 Pricing button clicked for tier:', tier.name, 'authenticated:', isAuthenticated);
                      const buttonText = getButtonText(tier);
                      console.log('📝 Button text:', buttonText);

                      // If button URL is set to "waitlist", open waitlist modal
                      if (tier.button_url === 'waitlist') {
                        e.preventDefault();
                        setWaitlistModal({
                          isOpen: true,
                          tierName: tier.display_name,
                          featureName: `${tier.display_name} Plan`
                        });
                        return;
                      }
                      // If button text is "Join Waitlist" (either custom or auto), open waitlist modal
                      if (buttonText === 'Join Waitlist') {
                        e.preventDefault();
                        setWaitlistModal({
                          isOpen: true,
                          tierName: tier.display_name,
                          featureName: `${tier.display_name} Plan`
                        });
                        return;
                      }
                      // If it's a custom button with no URL, also open waitlist modal
                      if (tier.is_button_enabled !== false && tier.button_text && !tier.button_url) {
                        e.preventDefault();
                        setWaitlistModal({
                          isOpen: true,
                          tierName: tier.display_name,
                          featureName: `${tier.display_name} Plan`
                        });
                        return;
                      }

                      // Handle current plan - do nothing
                      if (isCurrentPlan) {
                        e.preventDefault();
                        return;
                      }

                      // Handle free tier for authenticated users
                      if (isAuthenticated && tier.name === 'free') {
                        e.preventDefault();
                        navigate('/app/billing');
                        return;
                      }

                      // Handle downgrade for authenticated users
                      if (isAuthenticated && isDowngrade && tier.price_monthly >= 0) {
                        e.preventDefault();
                        // Open confirmation dialog
                        setDowngradeConfirm({
                          isOpen: true,
                          tier: tier
                        });
                        return;
                      }

                      // Handle upgrade/subscription for authenticated users
                      if (isAuthenticated && tier.price_monthly && tier.price_monthly > 0) {
                        e.preventDefault();
                        console.log('🔄 Starting subscription for tier:', tier.id, 'billing cycle:', billingCycle);
                        
                        // Track checkout initiation
                        try {
                          const { trackInitiateCheckout, trackSubscription } = await import('../lib/metaPixel');
                          const amount = billingCycle === 'annual' ? tier.price_annual : tier.price_monthly;
                          trackInitiateCheckout(amount, 'USD');
                          trackSubscription(tier.display_name, amount, 'USD');
                        } catch (error) {
                          console.error('Failed to track checkout initiation:', error);
                        }
                        
                        try {
                          await paymentService.subscribeToTier(tier.id, billingCycle);
                        } catch (error: any) {
                          console.error('❌ Failed to start subscription:', error);
                          toast.error(
                            `Failed to start subscription: ${error.response?.data?.detail || error.message || 'Please try again'}`,
                            {
                              duration: 5000,
                              position: 'top-center',
                            }
                          );
                        }
                        return;
                      }

                      // Handle non-authenticated users - navigate to register with tier info
                      if (!isAuthenticated) {
                        if (tier.name === 'free') {
                          navigate('/register');
                        } else {
                          navigate(`/register?tier=${tier.id}&billing=${billingCycle}`);
                        }
                        return;
                      }

                      console.log('🚫 No action taken. Conditions:', {
                        isAuthenticated,
                        tierName: tier.name,
                        isCurrentPlan,
                        isUpgrade,
                        isDowngrade,
                        priceMonthly: tier.price_monthly
                      });
                    };



                    return (
                      <div
                        key={tier.id}
                        className={`relative p-6 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col ${isCurrentPlan ? 'border-green-500 ring-2 ring-green-500' :
                          isPopular ? 'border-blue-500 ring-2 ring-blue-500' : ''
                          }`}
                      >
                        {isCurrentPlan && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-30">
                            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-2xl border border-green-400/50 backdrop-blur-sm whitespace-nowrap">
                              <span className="flex items-center space-x-1.5">
                                <Check className="w-3.5 h-3.5" />
                                <span>Current Plan</span>
                              </span>
                            </div>
                          </div>
                        )}
                        {!isCurrentPlan && isPopular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-30">
                            <div className="text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-2xl backdrop-blur-sm whitespace-nowrap" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', border: '1px solid rgba(48, 179, 221, 0.5)' }}>
                              <span className="flex items-center space-x-1.5">
                                <Crown className="w-3.5 h-3.5" />
                                <span>Most Popular</span>
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="text-center">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            {tier.display_name}
                          </h3>
                          <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 text-center">
                            {tier.description}
                          </p>
                          <div className="mt-6 flex items-baseline justify-center">
                            {tier.name === 'free' || tier.id === 'free' ? (
                              <div className="text-center">
                                <span className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                  Free
                                </span>
                              </div>
                            ) : tier.price_monthly === 0 && tier.price_yearly === 0 ? (
                              <div className="text-center">
                                <div className="relative">
                                  <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                    Coming Soon
                                  </span>
                                  <div className="absolute -top-1 -right-8">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                  Join waitlist for early access
                                </p>
                              </div>
                            ) : (
                              <>
                                <span className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                  {billingCycle === 'monthly' 
                                    ? formatPrice(tier.price_monthly, tier.name) 
                                    : formatPrice(tier.price_yearly ? Math.ceil(tier.price_yearly / 12) : 0, tier.name)}
                                </span>
                                {tier.name !== 'free' && tier.price_monthly && tier.price_monthly > 0 && (
                                  <span className="ml-1 text-lg font-semibold text-gray-600 dark:text-gray-400">
                                    /mo
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* ROI Messages */}
                        {tier.show_roi_texts && (tier.roi_text_1 || tier.roi_text_2) && (
                          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="space-y-2">
                              {tier.roi_text_1 && (
                                <div className="flex items-center text-sm text-green-700 font-medium">
                                  <span className="mr-2">💰</span>
                                  {tier.roi_text_1}
                                </div>
                              )}
                              {tier.roi_text_2 && (
                                <div className="flex items-center text-sm text-green-700 font-medium">
                                  <span className="mr-2">📈</span>
                                  {tier.roi_text_2}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Credits Info - Hidden as requested */}
                        {/* <div className="mt-6">
                          <div className="p-4 border border-blue-200/50 dark:border-blue-700/50 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(48, 179, 221, 0.1), rgba(74, 41, 138, 0.1))' }}>
                            ... credits display content ...
                          </div>
                        </div> */}

                        <ul role="list" className="mt-6 space-y-4 flex-1">
                          {tierFeatures.map((tierFeature) => {
                            // Handle both dynamic features and fallback string features
                            if (typeof tierFeature === 'string') {
                              return (
                                <li key={tierFeature} className="flex">
                                  <Check className="flex-shrink-0 w-6 h-6 text-green-500" aria-hidden="true" />
                                  <span className="ml-3 text-sm font-medium" style={{ color: '#000000' }}>{tierFeature}</span>
                                </li>
                              );
                            }

                            // Dynamic feature object
                            const displayText = tierFeature.display_text || tierFeature.pricing_feature.name;
                            const isIncluded = tierFeature.is_included;
                            const isHighlighted = tierFeature.is_highlighted;
                            const badgeText = tierFeature.badge_text;
                            const badgeColor = tierFeature.badge_color;

                            return (
                              <li key={tierFeature.id} className="flex items-start">
                                {isIncluded ? (
                                  <Check className="flex-shrink-0 w-6 h-6 text-green-500 mt-0.5" aria-hidden="true" />
                                ) : (
                                  <X className="flex-shrink-0 w-6 h-6 text-red-400 mt-0.5" aria-hidden="true" />
                                )}
                                <div className="ml-3 flex-1">
                                  <div className="flex items-center flex-wrap gap-2">
                                    <span className={`text-sm ${isIncluded ? 'font-medium' : 'text-gray-400 line-through'} ${isHighlighted ? 'font-semibold' : ''}`} style={isIncluded ? { color: '#000000' } : {}}>
                                      {displayText}
                                      {tierFeature.value && (
                                        <span className="text-blue-600 font-medium ml-1">
                                          ({tierFeature.value})
                                        </span>
                                      )}
                                    </span>
                                    {badgeText && (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor === 'green' ? 'bg-green-100 text-green-800' :
                                        badgeColor === 'orange' ? 'bg-orange-100 text-orange-800' :
                                          badgeColor === 'red' ? 'bg-red-100 text-red-800' :
                                            badgeColor === 'purple' ? 'bg-purple-100 text-purple-800' :
                                              badgeColor === 'gray' ? 'bg-gray-100 text-gray-800' :
                                                'bg-blue-100 text-blue-800'
                                        }`}>
                                        {badgeText}
                                      </span>
                                    )}
                                    {isHighlighted && (
                                      <Star className="inline-block w-4 h-4 text-yellow-500" />
                                    )}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>

                        {isCurrentPlan ? (
                          <button
                            disabled
                            className="mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium bg-green-100 text-green-800 cursor-not-allowed"
                          >
                            {getButtonText(tier)}
                          </button>
                        ) : tier.is_button_enabled !== false ? (
                          // Check if button is disabled
                          tier.button_url === 'disabled' ? (
                            <button
                              disabled
                              className="mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium bg-gray-300 text-gray-600 cursor-not-allowed"
                            >
                              {getButtonText(tier)}
                            </button>
                          ) :
                            // Check if it's a waitlist button (either "waitlist" URL, "Join Waitlist" text, or custom button with no URL)
                            (tier.button_url === 'waitlist' || getButtonText(tier) === 'Join Waitlist' || (tier.button_text && !tier.button_url)) ? (
                              <button
                                onClick={(e) => handleButtonClick(tier, e)}
                                className="mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium transition-colors text-white shadow-lg"
                                style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
                              >
                                {getButtonText(tier)}
                              </button>
                            ) : (
                              <button
                                onClick={(e) => handleButtonClick(tier, e)}
                                disabled={isCurrentPlan}
                                className={`mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium transition-colors ${isCurrentPlan
                                  ? 'bg-green-100 text-green-700 border-green-300 cursor-not-allowed'
                                  : isUpgrade
                                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                                    : isDowngrade
                                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
                                      : getButtonText(tier) === 'Back to Dashboard'
                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                        : isPopular
                                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                                          : getButtonText(tier) === 'Coming Soon'
                                            ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                            : 'bg-gray-800 text-white hover:bg-gray-900'
                                  }`}
                              >
                                {getButtonText(tier)}
                              </button>
                            )
                        ) : (
                          <button
                            disabled
                            className="mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium bg-gray-300 text-gray-600 cursor-not-allowed"
                          >
                            {tier.button_text || 'Not Available'}
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Compare Features Section - Hybrid Approach */}
            {allFeatures.length > 0 && (
              <div className="mt-16">
                <h2 className="text-3xl font-extrabold text-center" style={{ color: '#000000' }}>Compare Our Plans</h2>
                <div className="mt-8 overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 shadow-lg rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th scope="col" className="px-3 md:px-6 py-4 text-left text-xs md:text-sm font-semibold uppercase tracking-wider" style={{ color: '#000000' }}>Feature</th>
                        {tiers.map(tier => (
                          <th key={tier.id} scope="col" className="px-2 md:px-6 py-4 text-center text-xs md:text-sm font-semibold uppercase tracking-wider w-20 md:w-auto" style={{ color: '#000000' }}>
                            <span className="block md:hidden">{tier.display_name.split(' ')[0]}</span>
                            <span className="hidden md:block">{tier.display_name}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Monthly Credits Row - Hidden as requested */}
                      {/* <tr className="bg-blue-50/50">
                        <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-bold" style={{ color: '#000000' }}>
                          Monthly Credits
                        </td>
                        {tiers.map(tier => (
                          <td key={tier.id} className="px-2 md:px-6 py-3 md:py-4 text-xs md:text-sm text-center">
                            <div className="flex justify-center items-center min-h-[24px]">
                              <span className="font-bold text-black">
                                {tier.credits || tier.monthly_credits || '0'}
                              </span>
                            </div>
                          </td>
                        ))}
                      </tr> */}

                      {allFeatures.map((featureName, index) => {
                        // Define AI generation features that should show ✓/✗ instead of numbers
                        const aiGenerationFeatures = [
                          'image generation', 'image to 3d', '3d model', 'video generation',
                          'ai generation', 'brandify', 'ar generation', 'ar ad'
                        ];

                        // Define post creation features that should show ✓/✗ instead of numbers
                        const postCreationFeatures = [
                          'smart post creation', 'automated post scheduling', 'post creation', 'post scheduling'
                        ];

                        const isAIFeature = aiGenerationFeatures.some(ai =>
                          featureName.toLowerCase().includes(ai.toLowerCase())
                        );

                        const isPostFeature = postCreationFeatures.some(post =>
                          featureName.toLowerCase().includes(post.toLowerCase())
                        );

                        const isStorageFeature = featureName.toLowerCase().includes('storage');

                        return (
                          <tr key={featureName} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                            <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-medium" style={{ color: '#000000' }}>
                              {featureName}
                            </td>
                            {tiers.map(tier => {
                              const featureDisplay = getFeatureDisplay(featureName, tier);

                              // For AI features, convert numbers to ✓/✗
                              if (isAIFeature && featureDisplay.type === 'limit') {
                                const hasFeature = featureDisplay.display.props.children !== '0';
                                return (
                                  <td key={tier.id} className="px-2 md:px-6 py-3 md:py-4 text-xs md:text-sm text-center">
                                    <div className="flex justify-center items-center min-h-[24px]">
                                      {hasFeature ? (
                                        <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 mx-auto" />
                                      ) : (
                                        <X className="w-4 h-4 md:w-5 md:h-5 text-red-400 mx-auto" />
                                      )}
                                    </div>
                                  </td>
                                );
                              }

                              // For post creation features, convert numbers/unavailable to ✓/✗
                              if (isPostFeature && (featureDisplay.type === 'limit' || featureDisplay.type === 'unavailable')) {
                                const hasFeature = featureDisplay.type === 'limit' && featureDisplay.display.props.children !== '0';
                                return (
                                  <td key={tier.id} className="px-2 md:px-6 py-3 md:py-4 text-xs md:text-sm text-center">
                                    <div className="flex justify-center items-center min-h-[24px]">
                                      {hasFeature ? (
                                        <Check className="w-4 h-4 md:w-5 md:h-5 text-green-500 mx-auto" />
                                      ) : (
                                        <X className="w-4 h-4 md:w-5 md:h-5 text-red-400 mx-auto" />
                                      )}
                                    </div>
                                  </td>
                                );
                              }

                              // For storage features, add GB unit
                              if (isStorageFeature && featureDisplay.type === 'limit') {
                                const storageValue = featureDisplay.display.props.children;
                                return (
                                  <td key={tier.id} className="px-2 md:px-6 py-3 md:py-4 text-xs md:text-sm text-center">
                                    <div className="flex justify-center items-center min-h-[24px]">
                                      <span className="font-semibold text-black">
                                        {storageValue} GB
                                      </span>
                                    </div>
                                  </td>
                                );
                              }

                              // For non-AI features, show original display
                              return (
                                <td key={tier.id} className="px-2 md:px-6 py-3 md:py-4 text-xs md:text-sm text-center">
                                  <div className="flex justify-center items-center min-h-[24px]">
                                    {featureDisplay.display}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Included</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <X className="w-4 h-4 text-red-400" />
                    <span>Not Available</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-green-600">Unlimited</span>
                    <span>No limits</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900">Numbers</span>
                    <span>Usage limits</span>
                  </div>
                </div>
              </div>
            )}

            {/* Credit Packages Section - Hidden as requested */}
            {/* <div id="credit-packages" className="mt-20 mb-16 scroll-mt-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-extrabold text-black dark:text-white mb-4">
                  Need More Credits?
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                  Top up your account with credit packages. Credits never expire and work with any plan.
                </p>
              </div>

              <CreditPackages className="max-w-7xl mx-auto" />
            </div> */}

            {/* FAQ Section - SEO Optimized */}
            <div className="mt-20 mb-16">
              <StructuredFAQ
                faqs={pricingFAQs}
                title="Pricing & Features FAQ"
                className=""
              />


            </div>

          </div>
        </div>

        {/* Footer */}
        <footer className="bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Main Footer Content */}
            <div className="py-12 lg:py-16">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">

                {/* Company Info */}
                <div className="lg:col-span-1">
                  <div className="mb-6">
                    <img src="/logo-light.png" alt="Quixocial" className="h-12 w-auto block brightness-150 contrast-110" />
                  </div>
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    Intelligent Quixocial tools for modern creators, marketers, and businesses. Transform your content strategy with AI-powered solutions.
                  </p>
                  <div className="flex space-x-4">
                    <div className="flex items-center text-sm text-gray-400">
                      <Shield className="w-4 h-4 mr-2 text-green-400" />
                      <span>Enterprise Security</span>
                    </div>
                  </div>
                </div>

                {/* Product Links */}
                <div>
                  <h3 className="font-semibold text-white mb-6">Product</h3>
                  <ul className="space-y-4">
                    <li><Link to="/pricing" className="text-gray-400 hover:text-white transition-colors duration-200">Pricing</Link></li>
                    <li><Link to="/roadmap" className="text-gray-400 hover:text-white transition-colors duration-200">Roadmap</Link></li>
                    <li><Link to="/resources" className="text-gray-400 hover:text-white transition-colors duration-200">Resources</Link></li>
                  </ul>
                </div>

                {/* Company Links */}
                <div>
                  <h3 className="font-semibold text-white mb-6">Company</h3>
                  <ul className="space-y-4">
                    <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors duration-200">About</Link></li>
                    <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors duration-200">Blog</Link></li>
                    <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200">Contact</Link></li>
                    <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200">Help</Link></li>
                  </ul>
                </div>

                {/* Legal & Support */}
                <div>
                  <h3 className="font-semibold text-white mb-6">Legal</h3>
                  <ul className="space-y-4">
                    <li><Link to="/legal" className="text-gray-400 hover:text-white transition-colors duration-200">All Legal Documents</Link></li>
                    <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200">Privacy Policy</Link></li>
                    <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200">Terms of Service</Link></li>
                    <li><Link to="/data-deletion" className="text-gray-400 hover:text-white transition-colors duration-200">Data Deletion</Link></li>
                    <li><Link to="/cookies" className="text-gray-400 hover:text-white transition-colors duration-200">Cookie Policy</Link></li>
                    <li><Link to="/gdpr-request" className="text-gray-400 hover:text-white transition-colors duration-200">GDPR Request</Link></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom Footer */}
            <div className="border-t border-slate-800 py-8">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <div className="text-gray-400 text-sm">
                  &copy; 2025 Quixocial. All rights reserved.
                </div>
                <div className="flex items-center space-x-6 text-sm text-gray-400">
                  <div className="flex items-center">
                    <Globe className="w-4 h-4 mr-2" />
                    <span>Global CDN</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Waitlist Modal */}
        <WaitlistModal
          isOpen={waitlistModal.isOpen}
          onClose={() => setWaitlistModal({ ...waitlistModal, isOpen: false })}
          tierName={waitlistModal.tierName}
          featureName={waitlistModal.featureName}
        />

        {/* Downgrade Confirmation Dialog */}
        <Dialog open={downgradeConfirm.isOpen} onOpenChange={(open) => !open && setDowngradeConfirm({ isOpen: false, tier: null })}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-6 h-6 text-orange-500" />
                <DialogTitle>Confirm Downgrade</DialogTitle>
              </div>
              <DialogDescription className="pt-4">
                {downgradeConfirm.tier && (
                  <div className="space-y-4">
                    <p className="text-base">
                      Are you sure you want to downgrade to <span className="font-semibold">{downgradeConfirm.tier.display_name}</span>?
                    </p>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">What happens:</p>
                      <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1.5">
                        <li className="flex items-start">
                          <Check className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-blue-600" />
                          <span>You'll keep your current plan features until the end of your billing period</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-blue-600" />
                          <span>The downgrade will take effect on your next billing date</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-blue-600" />
                          <span>You can cancel this change anytime from your billing dashboard</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setDowngradeConfirm({ isOpen: false, tier: null })}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDowngradeConfirm}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                Confirm Downgrade
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </LightThemeWrapper>
  );
};

export default PricingPage;